import React, { useEffect, useState } from 'react';

import { isProd } from '@helpers/app.helper';
import { ThemeHelper } from '@helpers/theme.helper';

import { useAppSelector } from './hooks/appHooks';
import { selectThemeMode, selectVendor } from './store/appSlice/appSlice';

const fps = 60;
const duration = 2;
const easeInOutSine = (x: number) => -(Math.cos(Math.PI * x) - 1) / 2;
let themeInterval = 0;

const setAppTheme = (isDarkMode: boolean) =>
  document.body.classList.toggle(`theme-dark`, isDarkMode);

interface AppThemeProps {
  isLogin: boolean;
}

const AppTheme: React.FC<AppThemeProps> = ({ isLogin }) => {
  const vendor = useAppSelector(selectVendor);
  const themeMode = useAppSelector(selectThemeMode);
  const [themeHue, setThemeHue] = useState(Math.round(Math.random() * 360));

  useEffect(() => {
    const changeHandler = (e: MediaQueryListEvent) => setAppTheme(!!e.matches);
    const mq = window.matchMedia('(prefers-color-scheme: dark)');

    if (themeMode === ThemeHelper.ThemeModeConstants.SYSTEM)
      mq.addEventListener('change', changeHandler);

    setAppTheme(
      themeMode === ThemeHelper.ThemeModeConstants.SYSTEM
        ? mq.matches
        : themeMode === ThemeHelper.ThemeModeConstants.DARK,
    );

    return () => mq.removeEventListener('change', changeHandler);
  }, [themeMode]);

  useEffect(() => {
    if (vendor) setThemeHue(vendor.themeHue);
  }, [vendor]);

  const animate = () => {
    requestAnimationFrame(() => {
      if (isProd()) animatePos();
    });
  };

  const onSetThemeHue = () => {
    const colour = ThemeHelper.DemmiTheme.get(themeHue);
    ThemeHelper.setCSSPalette(colour.palette);
    ThemeHelper.setCSSVar(ThemeHelper.CSSVARS.THEME_BG_HUE, colour.hue);
    document.body.classList.toggle(
      `palette-light`,
      themeHue >= 60 && themeHue <= 180,
    );
    ThemeHelper.setCSSVar(
      ThemeHelper.CSSVARS.THEME_BG_01,
      `hsla(${colour.palette[400].h + 15}, ${colour.palette[400].s}%, ${
        colour.palette[400].l
      }%, 1)`,
    );
    ThemeHelper.setCSSVar(
      ThemeHelper.CSSVARS.THEME_BG_02,
      `hsla(${colour.palette[400].h - 15}, ${colour.palette[400].s}%, ${
        colour.palette[400].l
      }%, 1)`,
    );
  };

  let pos = 0;
  const animatePos = () => {
    pos += 1 / fps / duration;
    ThemeHelper.setCSSVar(
      ThemeHelper.CSSVARS.THEME_BG_POS,
      (0 + easeInOutSine(pos) * 100).toString(),
    );
  };

  useEffect(() => {
    if (!isLogin) onSetThemeHue();
  }, [themeHue]);

  useEffect(() => {
    themeInterval = window.setInterval(animate, 1000 / fps);
    return () => clearInterval(themeInterval);
  }, [isLogin]);

  useEffect(() => {
    return () => clearInterval(themeInterval);
  }, []);

  return <></>;
};

export default AppTheme;
